import * as THREE from 'three';

/**
 * Aventurero low-poly hecho con cajas y cilindros: sombrero, bufanda roja, mochila y farol.
 * Mira hacia -Z y tiene el origen en los pies. La animación es procedural: cada articulación
 * se acerca suavemente a la postura que toca (carrera, salto, deslizamiento, caída…).
 */
const mat = (color, extra) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });

function box(w, h, d, material, x = 0, y = 0, z = 0) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  m.position.set(x, y, z);
  return m;
}

function cylinder(rTop, rBottom, h, segments, material, x = 0, y = 0, z = 0) {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBottom, h, segments), material);
  m.position.set(x, y, z);
  return m;
}

function joint(parent, x, y, z) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
}

export class Adventurer {
  constructor() {
    this.jacket = mat(0x7a5530);
    const shirt = mat(0xdccca4);
    const pants = mat(0x36414c);
    const boots = mat(0x2b1d14);
    const skin = mat(0xe2b48e);
    const hat = (this.hat = mat(0x5c3c1f));
    const dark = mat(0x2a1a0e);
    const scarf = (this.scarf = mat(0xc63a2b));
    const pack = (this.pack = mat(0x6f5a35));
    this.baseGlow = new THREE.Color(0x000000);
    this.glass = mat(0xffd27a, { emissive: 0xffa53a });

    this.root = new THREE.Group(); // lo inclina Player al caer
    this.pose = joint(this.root, 0, 0, 0); // inclinación al deslizarse
    const hips = joint(this.pose, 0, 0.95, 0);

    // Tronco
    this.torso = joint(hips, 0, 0, 0);
    this.torso.add(box(0.44, 0.52, 0.26, this.jacket, 0, 0.3, 0));
    this.torso.add(box(0.2, 0.36, 0.02, shirt, 0, 0.34, -0.135));
    this.torso.add(box(0.47, 0.07, 0.28, dark, 0, 0.05, 0));
    this.torso.add(box(0.3, 0.09, 0.3, scarf, 0, 0.57, 0));
    this.scarfTail = joint(this.torso, 0.08, 0.56, 0.14);
    this.scarfTail.add(box(0.09, 0.035, 0.36, scarf, 0, 0, 0.18));
    // Mochila con manta enrollada y farol colgado
    this.torso.add(box(0.36, 0.4, 0.18, pack, 0, 0.3, 0.22));
    const roll = cylinder(0.08, 0.08, 0.42, 7, mat(0x8a2f2a), 0, 0.55, 0.22);
    roll.rotation.z = Math.PI / 2;
    this.torso.add(roll);
    this.torso.add(box(0.12, 0.14, 0.12, this.glass, 0.26, 0.16, 0.2), box(0.14, 0.03, 0.14, dark, 0.26, 0.245, 0.2));

    // Cabeza
    this.head = joint(this.torso, 0, 0.62, 0);
    this.head.add(box(0.26, 0.27, 0.26, skin, 0, 0.14, 0));
    this.head.add(box(0.06, 0.06, 0.05, skin, 0, 0.12, -0.15));
    this.head.add(box(0.27, 0.08, 0.1, mat(0x3a2414), 0, 0.22, 0.1)); // pelo por detrás
    this.head.add(cylinder(0.28, 0.28, 0.035, 10, hat, 0, 0.29, 0));
    this.head.add(cylinder(0.15, 0.17, 0.15, 8, hat, 0, 0.37, 0));
    this.head.add(cylinder(0.172, 0.172, 0.04, 8, dark, 0, 0.32, 0));

    // Brazos: hombro → codo → mano
    this.arms = [-1, 1].map((side) => {
      const shoulder = joint(this.torso, side * 0.29, 0.5, 0);
      shoulder.add(box(0.12, 0.3, 0.13, this.jacket, 0, -0.15, 0));
      const elbow = joint(shoulder, 0, -0.3, 0);
      elbow.add(box(0.11, 0.26, 0.12, shirt, 0, -0.13, 0));
      elbow.add(box(0.1, 0.1, 0.1, skin, 0, -0.3, 0));
      return { shoulder, elbow };
    });

    // Piernas: cadera → rodilla → bota
    this.legs = [-1, 1].map((side) => {
      const hip = joint(hips, side * 0.12, 0, 0);
      hip.add(box(0.16, 0.44, 0.18, pants, 0, -0.22, 0));
      const knee = joint(hip, 0, -0.44, 0);
      knee.add(box(0.14, 0.4, 0.16, pants, 0, -0.2, 0));
      knee.add(box(0.17, 0.12, 0.29, boots, 0, -0.45, -0.05));
      return { hip, knee };
    });

    this.phase = 0;
    this.time = 0;
  }

  /** Cambia los colores del traje (ver storage/Wardrobe.js). */
  applySkin(skin) {
    this.jacket.color.setHex(skin.jacket);
    this.scarf.color.setHex(skin.scarf);
    this.hat.color.setHex(skin.hat);
    this.pack.color.setHex(skin.pack);
    this.baseGlow.setHex(skin.glow ?? 0x000000);
    this.jacket.emissive.copy(this.baseGlow);
  }

  /** Lleva cada articulación hacia la postura del estado actual del jugador. */
  update(dt, p) {
    this.time += dt;
    const k = dt > 0 ? 1 - Math.exp(-16 * dt) : 1;
    const to = (obj, axis, target) => (obj.rotation[axis] += (target - obj.rotation[axis]) * k);

    let legs; // [caderaIzq, rodillaIzq, caderaDer, rodillaDer]
    let arms; // [hombroIzq, codoIzq, hombroDer, codoDer]
    let lean = -0.15;
    let slide = 0;
    let bob = 0;
    const t = this.time;

    if (p.state === 'falling') {
      // Agita brazos y piernas mientras cae.
      const f = Math.sin(t * 18);
      legs = [0.6 + f * 0.5, -0.8, -0.2 - f * 0.5, -0.6];
      arms = [2.6 + f * 0.5, 0.4, 2.6 - f * 0.5, 0.4];
      lean = 0.1;
    } else if (p.state === 'crashed' || p.state === 'caught') {
      legs = [0.3, -0.2, 0.1, -0.4];
      arms = [1.2, 0.3, 0.8, 0.5];
      lean = 0.2;
    } else if (p.state === 'idle') {
      legs = [0, 0, 0, 0];
      arms = [0.1, 0.2, 0.1, 0.2];
      lean = 0;
    } else if (p.sliding && p.grounded) {
      // Deslizamiento: tumbado hacia atrás con las piernas por delante.
      slide = 1;
      legs = [0.35, -0.15, 0.2, -0.35];
      arms = [0.25, 0.2, -0.05, 0.3]; // con la inclinación quedan casi horizontales, hacia delante
      lean = 0;
    } else if (!p.grounded) {
      // Salto: piernas recogidas, brazos arriba.
      legs = [1.1, -1.5, 0.3, -0.7];
      arms = [2.5, 0.4, 2.1, 0.6];
      lean = -0.05;
    } else {
      // Carrera: zancada proporcional a la velocidad.
      this.phase += dt * p.speed * 0.9;
      const s = Math.sin(this.phase);
      const kneeL = -Math.max(0, -Math.sin(this.phase - 0.7)) * 1.4 - 0.15;
      const kneeR = -Math.max(0, Math.sin(this.phase - 0.7)) * 1.4 - 0.15;
      legs = [s * 0.85, kneeL, -s * 0.85, kneeR];
      arms = [-s * 0.8, 1.1, s * 0.8, 1.1];
      bob = Math.abs(Math.cos(this.phase)) * 0.07;
    }

    to(this.legs[0].hip, 'x', legs[0]);
    to(this.legs[0].knee, 'x', legs[1]);
    to(this.legs[1].hip, 'x', legs[2]);
    to(this.legs[1].knee, 'x', legs[3]);
    to(this.arms[0].shoulder, 'x', arms[0]);
    to(this.arms[0].elbow, 'x', arms[1]);
    to(this.arms[1].shoulder, 'x', arms[2]);
    to(this.arms[1].elbow, 'x', arms[3]);
    to(this.torso, 'x', lean);
    to(this.pose, 'x', slide * 1.15);
    this.pose.position.z += (-slide * 0.45 - this.pose.position.z) * k;
    this.pose.position.y += (slide * 0.12 + bob - this.pose.position.y) * k;
    // Inclinación lateral al cambiar de carril.
    to(this.pose, 'z', Math.max(-0.35, Math.min(0.35, (p.x - p.lane * 1.3) * 0.3)));
    // La bufanda ondea al viento.
    this.scarfTail.rotation.x = -0.15 + Math.sin(t * 15) * 0.25;
    this.scarfTail.rotation.y = Math.sin(t * 9) * 0.3;
  }
}
